import { useParams, Link } from 'react-router-dom'
import { useState } from 'react'
import { Image, Calendar, Download, AlertTriangle, Flag, X, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDropByTokenQuery } from '@/data/contributor'
import { resolveAssetUrl } from '@/lib/assetUrl'
import { REPORT_REASONS, type ReportReason } from '@/lib/reportsStore'
import { useSubmitReportMutation } from '@/data/reports'
import { useToast } from '@/hooks/use-toast'

export default function PublicGalleryPage() {
  const { token } = useParams()
  const { contributor, isLoading: loading, isError } = useDropByTokenQuery(token, {
    includePhotos: true,
    refetchInterval: 30_000,
  })
  const drop = contributor?.drop ?? null
  const error = !token
    ? 'Invalid gallery link'
    : isError || (!loading && !drop)
    ? 'Gallery not found or is no longer public'
    : null

  // Report state
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportPhotoId, setReportPhotoId] = useState<string | null>(null)
  const [reportReason, setReportReason] = useState<ReportReason | null>(null)
  const [reportDescription, setReportDescription] = useState('')
  const [reportSubmitted, setReportSubmitted] = useState(false)
  const submitReportMutation = useSubmitReportMutation()
  const { toast } = useToast()

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Open report modal for a photo
  const openReportModal = (photoId: string) => {
    setReportPhotoId(photoId)
    setReportReason(null)
    setReportDescription('')
    setReportSubmitted(false)
    setShowReportModal(true)
  }

  // Close report modal
  const closeReportModal = () => {
    setShowReportModal(false)
    setReportPhotoId(null)
    setReportReason(null)
    setReportDescription('')
    setReportSubmitted(false)
    submitReportMutation.reset()
  }

  // Submit report through the upload-handler contributor session endpoint.
  const handleSubmitReport = async () => {
    if (!reportReason || !reportPhotoId) return

    // Backend stores a single `reason` text column; pack the structured
    // reason + free-form description into one string for moderators.
    const reasonText = reportDescription
      ? `${reportReason}: ${reportDescription}`
      : reportReason

    try {
      await submitReportMutation.mutateAsync({
        photoId: reportPhotoId,
        reason: reasonText,
        contributorToken: token,
      })
      setReportSubmitted(true)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to submit report',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading gallery...</p>
        </div>
      </div>
    )
  }

  // Error state
    if (error || !drop) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="h-16 w-16 bg-destructive/15 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="mt-6 text-2xl font-bold text-foreground">Gallery Not Available</h1>
            <p className="mt-2 text-muted-foreground">
              {error || 'This gallery link may have expired or been disabled by the owner.'}
            </p>
          <div className="mt-6">
            <Button asChild variant="outline">
              <Link to="/">Go to Homepage</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const photos = (drop.photos ?? []).map((p) => ({
    id: p.id,
    url: resolveAssetUrl(p.processed_url ?? p.original_url) ?? '',
    thumbnail: resolveAssetUrl(p.thumbnail_url ?? p.processed_url ?? p.original_url) ?? '',
  }))
  const photoCount = photos.length

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-xl font-bold text-primary">
                PhotoDrop
              </Link>
              <span className="text-muted-foreground">|</span>
              <span className="text-sm text-muted-foreground">Public Gallery</span>
            </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 bg-secondary/15 text-secondary rounded-full font-medium">
                  Read-only
                </span>
              </div>
            </div>
          </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Gallery header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">{drop.name}</h1>
          {drop.description && (
            <p className="mt-2 text-lg text-muted-foreground">{drop.description}</p>
          )}

          {/* Stats */}
          <div className="mt-4 flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              <span>{photoCount} photos</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Opens {formatDate(drop.opens_at ?? drop.opensAt ?? '')}</span>
            </div>
          </div>
        </div>

        {/* Photo grid */}
        {photoCount > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="aspect-square bg-muted rounded-lg overflow-hidden group relative"
              >
                <img
                  src={photo.url}
                  alt=""
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                {/* Overlay on hover - view only, no delete option */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <Button size="sm" variant="secondary" className="gap-2">
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="gap-2"
                    onClick={() => openReportModal(photo.id)}
                  >
                    <Flag className="h-4 w-4" />
                    Report
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-card text-card-foreground rounded-lg border p-12 text-center">
            <Image className="h-12 w-12 text-muted-foreground mx-auto" />
            <h3 className="mt-4 text-lg font-semibold">No photos yet</h3>
            <p className="mt-2 text-muted-foreground">
              Photos will appear here once contributors upload them.
            </p>
          </div>
        )}

          {/* Read-only notice */}
          <div className="mt-8 bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">
              This is a read-only gallery. You can view and download photos, but cannot upload or delete.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Want to create your own photo collection?{' '}
              <Link to="/signup" className="font-medium underline">
                Sign up for PhotoDrop
              </Link>
            </p>
          </div>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-muted-foreground">
            Powered by{' '}
            <Link to="/" className="font-medium text-primary hover:underline">
              PhotoDrop
            </Link>
            {' '}· Collect photos from your events effortlessly
          </p>
        </div>
      </footer>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={closeReportModal}
          />

          {/* Modal */}
          <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
            <button
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              onClick={closeReportModal}
            >
              <X className="h-5 w-5" />
            </button>

              {reportSubmitted ? (
                // Success state
                <div className="text-center py-6">
                  <div className="h-16 w-16 bg-secondary/15 rounded-full flex items-center justify-center mx-auto">
                    <Check className="h-8 w-8 text-secondary" />
                  </div>
                  <h2 className="mt-4 text-lg font-semibold">Report Submitted</h2>
                  <p className="mt-2 text-muted-foreground">
                    Thank you for helping keep PhotoDrop safe. Our team will review this report shortly.
                  </p>
                <Button className="mt-6" onClick={closeReportModal}>
                  Close
                </Button>
              </div>
              ) : (
                // Report form
                <>
                  <div className="flex items-center gap-3 text-foreground mb-4">
                    <Flag className="h-6 w-6 text-destructive" />
                    <h2 className="text-lg font-semibold">Report Content</h2>
                  </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Please select a reason for reporting this content. Our team will review it and take appropriate action.
                </p>

                {/* Reason selection */}
                <div className="space-y-2 mb-4">
                  {REPORT_REASONS.map((reason) => (
                    <label
                      key={reason.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        reportReason === reason.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 hover:bg-muted/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reportReason"
                        value={reason.id}
                        checked={reportReason === reason.id}
                        onChange={() => setReportReason(reason.id)}
                        className="sr-only"
                      />
                      <div
                        className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          reportReason === reason.id
                            ? 'border-primary'
                            : 'border-border'
                        }`}
                      >
                        {reportReason === reason.id && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="text-sm">{reason.label}</span>
                    </label>
                  ))}
                </div>

                {/* Optional description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">
                    Additional details (optional)
                  </label>
                  <textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder="Provide any additional context..."
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    rows={3}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={closeReportModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    onClick={handleSubmitReport}
                    disabled={!reportReason || submitReportMutation.isPending}
                  >
                    {submitReportMutation.isPending ? 'Submitting...' : 'Submit Report'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
